import "client-only";

import type { InstallReferrerPlugin } from "@/lib/capacitor-bridge";
import { errorCode, errorMessage } from "@/lib/error-message";
import { randomUuid } from "@/lib/random-uuid";

import { installReferrer } from "./plugin";
import {
  MAX_REPORT_ATTEMPTS,
  type RecordTokenResult,
  REPORT_DETAIL_MAX_LENGTH,
} from "./report";
import type { ReportBody } from "./report-schema";

/** The device side of Apple Ads attribution (ENG-266), in two steps:
 *
 *  1. REPORT, on first launch and before anyone signs in: AdServices token
 *     → `TOKEN_PATH`, where the server exchanges it with Apple and records
 *     the answer. First launch because that is the only moment every install
 *     passes through, including the ones that never sign up (the
 *     denominator of install-to-payer conversion).
 *  2. LINK, once someone is signed in: the install id → `CLAIM_PATH`, which
 *     ties the recorded install to the account.
 *
 *  Progress lives in localStorage, which the WKWebView keeps across launches
 *  and app updates (a delete clears it). iOS may also reclaim web view
 *  storage under disk pressure, after which the install reports again under
 *  a new id: one more row for the same phone. */

/** Public: posted before anyone signs in. Excluded from the proxy matcher. */
export const TOKEN_PATH = "/api/attribution/apple-ads/token";
/** Authed: links the recorded install to the signed-in account. */
export const CLAIM_PATH = "/api/attribution/apple-ads/claim";

const STORAGE_KEY = "mc_apple_ads_attribution";

export type AttributionState = {
  /** Generated on the device and stored BEFORE the first report, so a
   *  report repeated after a lost response lands on the same row. */
  installId: string;
  recorded: boolean;
  linked: boolean;
  attempts: number;
};

export type AttributionDeps = {
  storage: Pick<Storage, "getItem" | "setItem">;
  plugin: () => InstallReferrerPlugin | null;
  fetch: typeof fetch;
  newId: () => string;
};

export function readState(
  storage: AttributionDeps["storage"],
): AttributionState | null {
  try {
    const value = JSON.parse(storage.getItem(STORAGE_KEY) ?? "null") as Partial<
      Record<keyof AttributionState, unknown>
    > | null;
    if (
      typeof value?.installId === "string" &&
      typeof value.attempts === "number"
    ) {
      return {
        installId: value.installId,
        recorded: value.recorded === true,
        linked: value.linked === true,
        attempts: value.attempts,
      };
    }
  } catch {
    // Unreadable storage or a corrupt value: start over.
  }
  return null;
}

/** False when storage refused the write (unavailable, or over quota). */
function writeState(
  storage: AttributionDeps["storage"],
  state: AttributionState,
): boolean {
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(state));
    return true;
  } catch {
    return false;
  }
}

function postJson(deps: AttributionDeps, path: string, body: unknown) {
  return deps.fetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

/** Capacitor rejects a plugin call with the plugin's `code` and a message.
 *  For any AdServices failure the plugin sends the one code
 *  IOS_ATTRIBUTION_ERROR and the native error's localized description, so
 *  the AAAttribution error is only in the message. */
function describePluginError(err: unknown): string {
  const text = [errorCode(err), errorMessage(err)].filter(Boolean).join(": ");
  return (text || "unknown").slice(0, REPORT_DETAIL_MAX_LENGTH);
}

/** Step 1. Resolves to the state after this launch's attempt; null off the
 *  shell, on builds without the plugin, and when storage cannot keep a new
 *  install id. Never rejects. */
export async function reportInstall(
  deps: AttributionDeps,
): Promise<AttributionState | null> {
  const plugin = deps.plugin();
  if (!plugin) return null;
  const prior = readState(deps.storage);
  if (prior?.recorded) return prior;
  if (prior && prior.attempts >= MAX_REPORT_ATTEMPTS) return prior;

  // Counted BEFORE the request, so an attempt cut short by a reload (the
  // OAuth return is a full navigation) still counts toward the cap.
  let state: AttributionState = {
    installId: prior?.installId ?? deps.newId(),
    recorded: false,
    linked: false,
    attempts: (prior?.attempts ?? 0) + 1,
  };
  // Never send an install id this device could not store: every launch
  // would mint another, with no cap (the count is not stored either), and
  // one phone would become a new row per launch. An id already stored is
  // safe to send again even when this write failed.
  const counted = writeState(deps.storage, state);
  if (!counted && !prior) return null;
  // A device with no token still reports, so the failure is counted; the
  // route answers it `recorded: false`. Any failure is already counted on
  // the device, and a later launch tries again until the cap.
  let body: ReportBody;
  try {
    const token = (await plugin.getReferrer()).attributionToken;
    body = token
      ? { installId: state.installId, token, attempt: state.attempts }
      : { deviceError: "no_token", attempt: state.attempts };
  } catch (err) {
    body = { deviceError: describePluginError(err), attempt: state.attempts };
  }
  // Uncounted, the cap never comes: the same error would post on every page
  // load for good. A token still goes, since it can get the install recorded.
  if (!counted && !("token" in body)) return state;
  try {
    const res = await postJson(deps, TOKEN_PATH, body);
    const result = (await res.json()) as RecordTokenResult;
    // Only a token can have been recorded, whatever the answer says.
    if (result.recorded && "token" in body) {
      state = { ...state, recorded: true };
      writeState(deps.storage, state);
    }
  } catch {
    // The request failed.
  }
  return state;
}

/** Step 2. Waits for this document's report, then links only a RECORDED
 *  install: linking before the row exists would match nothing, and the
 *  device would mark it linked anyway. Reads storage rather than the
 *  report's result, which goes stale once a link succeeds and the authed
 *  layout mounts again. A failed or signed-out request leaves it unlinked
 *  for the next mount. Never rejects. */
export async function linkInstall(
  deps: AttributionDeps,
  reported: Promise<unknown>,
): Promise<void> {
  await reported;
  const state = readState(deps.storage);
  if (!state?.recorded || state.linked) return;
  try {
    const res = await postJson(deps, CLAIM_PATH, { id: state.installId });
    if (res.ok) writeState(deps.storage, { ...state, linked: true });
  } catch {
    // Network failure: the next mount tries again.
  }
}

function browserDeps(): AttributionDeps {
  return {
    // Reached lazily: the `localStorage` getter itself can throw, and here
    // that lands inside readState/writeState's try.
    storage: {
      getItem: (key) => window.localStorage.getItem(key),
      setItem: (key, value) => window.localStorage.setItem(key, value),
    },
    plugin: installReferrer,
    fetch: (...args) => window.fetch(...args),
    newId: randomUuid,
  };
}

/** One report per document: the root layout starts it, and the authed
 *  layout's link waits on the same promise instead of racing a second
 *  report. */
let reported: Promise<AttributionState | null> | null = null;

export function reportInstallOnce(): Promise<AttributionState | null> {
  reported ??= reportInstall(browserDeps());
  return reported;
}

/** One claim in flight at a time: the finish page and the authed layout can
 *  both mount while the report is still waiting on Apple. Cleared once it
 *  settles, so a later mount can retry a failed claim. */
let linking: Promise<void> | null = null;

export function linkInstallOnce(): Promise<void> {
  linking ??= linkInstall(browserDeps(), reportInstallOnce()).finally(() => {
    linking = null;
  });
  return linking;
}
